import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderPlus, Plus, Trash2, ExternalLink, Settings2, FileText, Link2, ChevronRight, RefreshCw, Pencil, Check, X, BellRing, Grid3x3, ClipboardCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useFeatureFlags } from "@/lib/featureFlags";
import { formatDateOnly, formatDateTime } from "@/lib/date";
import GoogleDriveCard from "./GoogleDriveCard";
import AssignmentPicker from "./AssignmentPicker";
import DocStatusBadge from "./DocStatusBadge";

export default function CoachDepot() {
  const { document_upload: uploadsEnabled } = useFeatureFlags();
  const [drive, setDrive] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [subs, setSubs] = useState([]);
  const [categories, setCategories] = useState([]);

  const loadRefs = useCallback(async () => {
    try {
      const reqs = [api.get("/coach/subgroups")];
      if (uploadsEnabled) reqs.push(api.get("/document-depot/categories"));
      const [s, c] = await Promise.all(reqs);
      setSubs(s.data.items || []);
      if (c) setCategories(c.data.items || []);
    } catch { /* noop */ }
    if (uploadsEnabled) {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_BACKEND_URL}/api/oauth/drive/status`);
        setDrive(data);
      } catch { /* noop */ }
    }
  }, [uploadsEnabled]);

  useEffect(() => { loadRefs(); }, [loadRefs]);

  return (
    <div data-testid="coach-document-depot">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Document Depot</h1>
          <p className="text-sm text-slate-500">Assign forms, track submissions, and share handouts with your team.</p>
        </div>
        {uploadsEnabled && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowSettings((v) => !v)} data-testid="depot-settings-btn">
            <Settings2 className="mr-2 h-4 w-4" /> Settings
          </Button>
        )}
      </div>

      {uploadsEnabled && showSettings && (
        <div data-testid="depot-settings-panel">
          <GoogleDriveCard onChange={setDrive} />
          {drive?.connected && <CategoryManager categories={categories} reload={loadRefs} />}
        </div>
      )}

      <Tabs defaultValue="forms">
        <TabsList className="mb-4" data-testid="depot-tabs">
          <TabsTrigger value="forms" data-testid="depot-tab-forms">Forms</TabsTrigger>
          <TabsTrigger value="handouts" data-testid="depot-tab-handouts">Handouts</TabsTrigger>
        </TabsList>
        <TabsContent value="forms">
          <FormsTab subs={subs} categories={categories} uploadsEnabled={uploadsEnabled} driveConnected={drive?.connected} reloadCategories={loadRefs} />
        </TabsContent>
        <TabsContent value="handouts">
          <HandoutsTab subs={subs} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CategoryManager({ categories, reload }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/document-depot/categories", { name: name.trim() }); toast.success("Folder created."); setName(""); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    try { await api.put(`/document-depot/categories/${id}`, { name: editName.trim() }); toast.success("Folder renamed."); setEditId(null); setEditName(""); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/document-depot/categories/${id}`); toast.success("Folder deleted."); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Card className="mb-6 p-5" data-testid="category-manager">
      <p className="mb-1 font-heading font-semibold text-slate-800">Destination folders</p>
      <p className="mb-3 text-sm text-slate-500">Create folders in your Drive to collect member uploads, then pick one when creating a form.</p>
      <div className="mb-3 flex flex-col gap-2">
        {categories.length === 0 && <span className="text-sm text-slate-400" data-testid="no-categories">No folders yet.</span>}
        {categories.map((c) => (
          <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
            <FolderPlus className="h-4 w-4 shrink-0 text-orange-500" />
            {editId === c.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" data-testid={`category-edit-input-${c.id}`} onKeyDown={(e) => e.key === "Enter" && saveEdit(c.id)} autoFocus />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600" onClick={() => saveEdit(c.id)} data-testid={`category-save-${c.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => { setEditId(null); setEditName(""); }}><X className="h-4 w-4" /></Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium text-slate-700">{c.name}</span>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-500" onClick={() => { setEditId(c.id); setEditName(c.name); }} data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => remove(c.id)} data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Waivers, Photos" data-testid="category-name-input" onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button onClick={add} disabled={busy} data-testid="category-add-btn" className="shrink-0">{busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Plus className="mr-2 h-4 w-4" /> Add</>}</Button>
      </div>
    </Card>
  );
}

function FormsTab({ subs, categories, uploadsEnabled, driveConnected, reloadCategories }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [pending, setPending] = useState(0);
  const load = useCallback(() => {
    api.get("/document-depot/forms").then(({ data }) => {
      setItems(data.items || []);
      setPending((data.items || []).reduce((n, f) => n + (f.pending_count || 0), 0));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    try { await api.delete(`/document-depot/forms/${id}`); toast.success("Form deleted."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div data-testid="forms-tab">
      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        <Link to="/coach/document-grid"><Button variant="outline" className="rounded-full" data-testid="members-grid-btn"><Grid3x3 className="mr-2 h-4 w-4" /> Members grid</Button></Link>
        <Link to="/coach/document-review"><Button variant="outline" className="rounded-full" data-testid="review-btn"><ClipboardCheck className="mr-2 h-4 w-4" /> Review{pending ? ` (${pending})` : ""}</Button></Link>
        <NewFormDialog open={open} setOpen={setOpen} subs={subs} categories={categories} uploadsEnabled={uploadsEnabled} driveConnected={driveConnected} reloadCategories={reloadCategories} onCreated={load} />
      </div>
      {items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="forms-empty">No forms yet.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} className="p-4" data-testid={`form-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <button className="flex-1 text-left" onClick={() => setActive(r)} data-testid={`form-open-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-500">{r.seq}</span>
                    <FileText className="h-4 w-4 text-orange-500" />
                    <span className="font-semibold text-slate-800">{r.name}</span>
                  </div>
                  {r.description && <p className="mt-1 text-sm text-slate-500">{r.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>👥 {r.assign_label}</span>
                    {r.due_date && <span>Due {formatDateOnly(r.due_date)}</span>}
                    <span className="font-semibold text-emerald-700" data-testid={`form-count-${r.id}`}>{r.approved_count}/{r.assigned_count} approved</span>
                    {r.pending_count > 0 && <span className="font-semibold text-sky-700">{r.pending_count} awaiting review</span>}
                  </div>
                </button>
                <div className="flex items-center gap-1">
                  {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer" data-testid={`form-link-${r.id}`} onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="icon" title="Open document"><ExternalLink className="h-4 w-4" /></Button></a>}
                  <Button variant="ghost" size="icon" onClick={() => setActive(r)} data-testid={`form-view-${r.id}`}><ChevronRight className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => remove(r.id)} data-testid={`form-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {active && <SubmissionsDialog form={active} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function NewFormDialog({ open, setOpen, subs, categories, uploadsEnabled, driveConnected, reloadCategories, onCreated }) {
  const [form, setForm] = useState({ name: "", link: "", description: "", due_date: "", category_id: "" });
  const [assign, setAssign] = useState({ assign_type: "team", subgroup_ids: [] });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Enter a form title.");
    if (!form.link.trim()) return toast.error("Paste a link to the shared document.");
    setBusy(true);
    try {
      await api.post("/document-depot/forms", {
        name: form.name, link: form.link, description: form.description,
        due_date: form.due_date || null, category_id: form.category_id || null, ...assign,
      });
      toast.success("Form created.");
      setOpen(false);
      setForm({ name: "", link: "", description: "", due_date: "", category_id: "" });
      setAssign({ assign_type: "team", subgroup_ids: [] });
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-form-btn"><Plus className="mr-2 h-4 w-4" /> New form</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="new-form-dialog">
        <DialogHeader><DialogTitle>New form</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Signed medical waiver" data-testid="form-name-input" /></div>
          <div><Label>Link to shared document</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://drive.google.com/…" data-testid="form-link-input" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief instructions" data-testid="form-desc-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="form-due-input" /></div>
            <div><Label>Assigned to</Label><AssignmentPicker value={assign} onChange={setAssign} subs={subs} testId="form-assign" /></div>
          </div>
          {uploadsEnabled && (
            <DestinationFolderField
              value={form.category_id}
              onChange={(v) => setForm({ ...form, category_id: v })}
              categories={categories}
              driveConnected={driveConnected}
              reloadCategories={reloadCategories}
            />
          )}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="form-submit-btn">{busy ? "Creating…" : "Create form"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DestinationFolderField({ value, onChange, categories, driveConnected, reloadCategories }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const createFolder = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/document-depot/categories", { name });
      toast.success("Folder created.");
      await reloadCategories?.();
      onChange(data.id);
      setCreating(false);
      setNewName("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Label>Destination folder <span className="font-normal text-slate-400">(optional — for member uploads)</span></Label>
      {!driveConnected ? (
        <p className="text-xs text-amber-600" data-testid="form-folder-connect-hint">Connect your Google Drive in Settings to collect member uploads.</p>
      ) : creating ? (
        <div className="flex gap-2" data-testid="form-new-folder-row">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Waivers, Photos"
            data-testid="form-new-folder-input" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createFolder(); } }} />
          <Button type="button" onClick={createFolder} disabled={busy} className="shrink-0" data-testid="form-new-folder-create-btn">
            {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => { setCreating(false); setNewName(""); }} className="shrink-0" data-testid="form-new-folder-cancel-btn">Cancel</Button>
        </div>
      ) : (
        <Select
          value={value || "__none__"}
          onValueChange={(v) => {
            if (v === "__create__") setCreating(true);
            else onChange(v === "__none__" ? "" : v);
          }}
        >
          <SelectTrigger data-testid="form-category-select"><SelectValue placeholder="Choose folder (optional)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__" data-testid="form-category-none">No folder</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`form-category-${c.id}`}>{c.name}</SelectItem>)}
            <SelectItem value="__create__" data-testid="form-category-create-new" className="font-medium text-orange-600">
              <span className="flex items-center gap-1"><FolderPlus className="h-4 w-4" /> Create new folder…</span>
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function SubmissionsDialog({ form, onClose }) {
  const [rows, setRows] = useState([]);
  const [returnFor, setReturnFor] = useState(null);
  const [note, setNote] = useState("");
  const [owesOnly, setOwesOnly] = useState(false);
  const [nudging, setNudging] = useState(false);
  const load = useCallback(() => {
    api.get(`/document-depot/forms/${form.id}/submissions`).then(({ data }) => setRows(data.rows || [])).catch(() => {});
  }, [form.id]);
  useEffect(() => { load(); }, [load]);
  const OWES = ["not_submitted", "returned", "rejected"];
  const oweCount = rows.filter((r) => r.cell !== "approved").length;
  const visible = owesOnly ? rows.filter((r) => OWES.includes(r.status) || r.cell === "overdue") : rows;
  const act = async (memberId, action, noteVal) => {
    try { await api.post(`/document-depot/forms/${form.id}/members/${memberId}/${action}`, action === "approve" ? undefined : { note: noteVal || "" }); toast.success("Updated."); setReturnFor(null); setNote(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const nudgeAll = async () => {
    setNudging(true);
    try { const { data } = await api.post(`/document-depot/forms/${form.id}/nudge`); toast.success(data.nudged ? `Nudged ${data.nudged} member(s).` : "Everyone's approved — no one to nudge."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setNudging(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="submissions-dialog">
        <DialogHeader><DialogTitle>{form.name} — submissions</DialogTitle><DialogDescription>Approve or return this form for each team member.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
          <span className="text-sm font-medium text-slate-700" data-testid="owe-summary">{oweCount === 0 ? "Everyone's approved 🎉" : `${oweCount} of ${rows.length} not yet approved`}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" className={`h-8 rounded-full ${owesOnly ? "bg-slate-200" : ""}`} onClick={() => setOwesOnly((v) => !v)} data-testid="owes-only-toggle">{owesOnly ? "Show all" : "Show who owes"}</Button>
            <Button size="sm" onClick={nudgeAll} disabled={nudging || oweCount === 0} data-testid="nudge-all-btn" className="h-8 rounded-full">{nudging ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <BellRing className="mr-2 h-4 w-4" />} Nudge all</Button>
          </div>
        </div>
        <div className="max-h-[55vh] space-y-2 overflow-y-auto">
          {visible.map((row) => (
            <div key={row.member_id} className="flex items-center justify-between gap-2 rounded-lg border p-3" data-testid={`submission-row-${row.member_id}`}>
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-800">{row.member_name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <DocStatusBadge status={row.cell === "overdue" ? "overdue" : row.status} testId={`submission-status-${row.member_id}`} />
                  {row.submitted_at && <span className="text-xs text-slate-500" data-testid={`submission-submitted-${row.member_id}`}>Submitted {formatDateTime(row.submitted_at, { dateStyle: "medium", timeStyle: "short" })}</span>}
                  {row.approved_at && <span className="text-xs font-medium text-emerald-600" data-testid={`submission-approved-${row.member_id}`}>Approved {formatDateTime(row.approved_at, { dateStyle: "medium", timeStyle: "short" })}</span>}
                  {row.web_view_link && <a href={row.web_view_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 hover:underline" data-testid={`submission-view-${row.member_id}`}>File <ExternalLink className="h-3 w-3" /></a>}
                </div>
                {row.note && <p className="mt-1 text-xs text-amber-700">Note: {row.note}</p>}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="sm" variant="outline" className="h-8 text-emerald-600" disabled={row.status === "approved"} onClick={() => act(row.member_id, "approve")} data-testid={`approve-${row.member_id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="h-8 text-amber-600" onClick={() => setReturnFor(row.member_id)} data-testid={`return-${row.member_id}`}>Return</Button>
              </div>
            </div>
          ))}
          {visible.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No members to show.</p>}
        </div>
        {returnFor && (
          <div className="rounded-lg border bg-amber-50 p-3" data-testid="return-note-box">
            <Label>Reason for return</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Explain what needs fixing…" data-testid="return-note-input" />
            <div className="mt-2 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setReturnFor(null); setNote(""); }}>Cancel</Button>
              <Button size="sm" onClick={() => act(returnFor, "return", note)} data-testid="return-confirm-btn">Return for changes</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const HANDOUT_TYPE_OPTIONS = [
  { value: "weekly_newsletter", label: "Weekly Newsletter" },
  { value: "event_planner", label: "Event Planner" },
  { value: "posting", label: "Posting" },
  { value: "trip_info", label: "Trip Information & Itinerary" },
  { value: "flyer", label: "Flyer" },
];

function HandoutsTab({ subs }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/document-depot/handouts").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    try { await api.delete(`/document-depot/handouts/${id}`); toast.success("Handout removed."); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div data-testid="handouts-tab">
      <div className="mb-4 flex justify-end"><NewHandoutDialog open={open} setOpen={setOpen} subs={subs} onCreated={load} /></div>
      {items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500" data-testid="handouts-empty">No handouts yet.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <Card key={r.id} className="flex items-center justify-between gap-3 p-4" data-testid={`handout-card-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2"><Link2 className="h-4 w-4 text-orange-500" /><span className="font-semibold text-slate-800">{r.name}</span></div>
                <p className="mt-1 text-xs text-slate-500"><span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{r.type_label}</span> · 👥 {r.assign_label}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {r.link && <a href={r.link} target="_blank" rel="noopener noreferrer" data-testid={`handout-open-${r.id}`}><Button variant="outline" size="sm" className="rounded-full"><ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open</Button></a>}
                <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600" onClick={() => remove(r.id)} data-testid={`handout-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NewHandoutDialog({ open, setOpen, subs, onCreated }) {
  const [form, setForm] = useState({ handout_type: "weekly_newsletter", name: "", link: "" });
  const [assign, setAssign] = useState({ assign_type: "team", subgroup_ids: [] });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Enter a name.");
    if (!form.link.trim()) return toast.error("Paste a link.");
    setBusy(true);
    try {
      await api.post("/document-depot/handouts", { ...form, ...assign });
      toast.success("Handout shared.");
      setOpen(false);
      setForm({ handout_type: "weekly_newsletter", name: "", link: "" });
      setAssign({ assign_type: "team", subgroup_ids: [] });
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-handout-btn"><Plus className="mr-2 h-4 w-4" /> New handout</Button></DialogTrigger>
      <DialogContent data-testid="new-handout-dialog">
        <DialogHeader><DialogTitle>Share a handout</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={form.handout_type} onValueChange={(v) => setForm({ ...form, handout_type: v })}>
              <SelectTrigger data-testid="handout-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HANDOUT_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`handout-type-${o.value}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Week 3 Newsletter" data-testid="handout-name-input" /></div>
          <div><Label>Link</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://drive.google.com/…" data-testid="handout-link-input" /></div>
          <div><Label>Share with</Label><AssignmentPicker value={assign} onChange={setAssign} subs={subs} testId="handout-assign" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} data-testid="handout-submit-btn">{busy ? "Sharing…" : "Share handout"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
